<template>
  <div class="input-curve">
    <!-- CURVE TYPE selection box -->
    <input-dropdown
      v-if="typeEditable"
      :select="state.curveType"
      :options="curveNames"
      :placeholder="$t('labels.chooseTemplate')"
      :prop="null"
      @select="onSelectCurveType"
    />

    <div
      v-if="state.curveType === 'formula' && curveEditable"
      class="line pv-2"
    >
      <span>f(x)=</span>
      <input-text
        :value="state.localFormula"
        :error="state.formulaHasError ? $t('errors.formulaInvalid') : ''"
        @update:value="updateFormula($event)"
      />
    </div>

    <!-- CURVE -->
    <svg
      ref="graphRef"
      :viewBox="`0 0 ${WIDTH + padding.left + padding.right} ${
        height + padding.bottom + padding.top
      }`"
      class="input-curve__graph mt-2"
      @pointermove="setXAtMouse"
      @click.ctrl="onRightClick"
      @contextmenu="onMainContextMenu"
    >
      <g :transform="`translate(-1, ${padding.top})`">
        <text
          v-for="(i, index) in verticalNumbers"
          :key="'label-' + i"
          class="input-curve__label input-curve__label_v"
          :x="padding.left"
          :y="heightSteps[index]"
        >
          {{ i }}
        </text>
      </g>
      <g :transform="`translate(${padding.left}, ${padding.top + height + 7})`">
        <g
          v-for="(i, index) in horizontalNumbers"
          :key="'label-' + i"
          :transform="`translate(${widthSteps[index]}, ${
            labelsRotated ? -2 : 0
          })`"
        >
          <text
            class="input-curve__label input-curve__label_h"
            :class="{ '-rotated': labelsRotated }"
            x="0"
            y="0"
          >
            {{ i }}
          </text>
        </g>
      </g>
      <g :transform="`translate(${padding.left}, ${padding.top})`">
        <!-- horizontal lines -->
        <line
          v-for="i in heightSteps"
          :key="'rh-' + i"
          class="input-curve__raster-line"
          :x1="0"
          :y1="i"
          :x2="WIDTH"
          :y2="i"
          vector-effect="non-scaling-stroke"
        />

        <!-- vertical lines -->
        <line
          v-for="i in widthSteps"
          :key="'rv-' + i"
          class="input-curve__raster-line"
          :x1="i"
          :y1="-10"
          :x2="i"
          :y2="height"
          vector-effect="non-scaling-stroke"
        />

        <!-- indicator line -->
        <line
          v-if="value != null"
          class="input-curve__line_value"
          :x1="scaleXValue(value)"
          :y1="0"
          :x2="scaleXValue(value)"
          :y2="height"
          vector-effect="non-scaling-stroke"
        />

        <line
          v-if="value != null"
          class="input-curve__mouseline"
          :x1="scaleXValue(state.mouseX)"
          :y1="0"
          :x2="scaleXValue(state.mouseX)"
          :y2="height"
          vector-effect="non-scaling-stroke"
        />

        <!-- color areas -->
        <template v-if="colorAreasVisible">
          <rect
            v-for="(area, index) in horizontalAreas"
            :key="'area' + index + area"
            :fill="area"
            :x="index * (WIDTH / horizontalAreas.length)"
            :y="height - 5"
            :width="WIDTH / horizontalAreas.length"
            height="5"
            style="opacity: 0.7"
          />
        </template>

        <line
          class="input-curve__border-line"
          x1="0"
          y1="0"
          x2="0"
          :y2="height"
          vector-effect="non-scaling-stroke"
        />
        <line
          class="input-curve__border-line"
          x1="0"
          :y1="height"
          :x2="WIDTH"
          :y2="height"
          vector-effect="non-scaling-stroke"
        />

        <!--
          This group is mirrored vertically;
          that way the coordinates correspond to values.
        -->
        <g
          v-if="state.points.length > 0"
          :transform="`translate(0, ${
            missingVerticalAmount + Y_MAX + roundedOffsetY
          }) scale(1,-1)`"
        >
          <!-- LINE CUTOFFS -->
          <!-- left -->
          <line
            v-if="startSlopeLine.x2 > 0"
            :x1="startSlopeLine.x1"
            :x2="startSlopeLine.x2"
            :y1="startSlopeLine.y1"
            :y2="startSlopeLine.y2"
            class="input-curve__line"
            vector-effect="non-scaling-stroke"
          />
          <!-- right -->
          <line
            v-if="endSlopeLine.x1 < 100"
            :x1="endSlopeLine.x1"
            :x2="endSlopeLine.x2"
            :y1="endSlopeLine.y1"
            :y2="endSlopeLine.y2"
            class="input-curve__line"
            vector-effect="non-scaling-stroke"
          />

          <!-- the MAIN CURVE path line -->
          <path
            :d="pathDescription"
            class="input-curve__line"
            vector-effect="non-scaling-stroke"
          />

          <!-- HANDLES -->
          <template v-if="isGraphEditable">
            <!-- first point -->
            <input-curve-handle
              :key="
                state.points[0].id == null
                  ? `pointindex-0`
                  : `point-${state.points[0].id}`
              "
              :point="state.points[0]"
              :pointid="state.points[0].id == null ? 0 : state.points[0].id"
              :max-y="fixedStart ? 0 : pointMaxY"
              :min-y="fixedStart ? 0 : pointMinY"
              :min-x="xMin"
              :max-x="xBezierLimits[0].right"

              :start-x-value="startXValue"
              :end-x-value="endXValue"
              
              :bezier-min-x="xBezierLimits[0].left"
              :bezier-max-x="xBezierLimits[0].right"
              :grid-column-width="widthStepSize"
              :deletable="false"
              :y-factor="state.yFactor"
              :move-dimensions="moveDimensions"
              :small="state.curveType == 'formula'"
              @move-point="setPoint(0, $event)"
              @dragend="update()"
              @contextmenu="openContextMenu($event)"
            />

            <!-- middle points -->
            <template v-if="state.points.length > 1">
              <input-curve-handle
                v-for="(point, index) in state.points.slice(1, -1)"
                :key="
                  point.id == null
                    ? `pointindex-${index + 1}`
                    : `point-${point.id}`
                "
                :pointid="point.id"
                :point="point"
                :max-y="pointMaxY"
                :min-y="pointMinY"
                :min-x="xBezierLimits[index + 1].left"
                :max-x="xBezierLimits[index + 1].right"
                :start-x-value="startXValue"
                :end-x-value="endXValue"
                :bezier-min-x="xBezierLimits[index + 1].left"
                :bezier-max-x="xBezierLimits[index + 1].right"
                :grid-column-width="widthStepSize"
                :y-factor="state.yFactor"
                :move-dimensions="moveDimensions"
                :small="state.curveType == 'formula'"
                @move-point="setPoint(index + 1, $event)"
                @dragend="update()"
                @delete="deletePoint(index + 1)"
                @contextmenu="openContextMenu($event)"
              />

              <!-- last point -->
              <input-curve-handle
                :key="
                  state.points[state.points.length - 1].id == null
                    ? `pointindex-${state.points.length - 1}`
                    : `pt-${state.points[state.points.length - 1].id}`
                "
                :point="state.points[state.points.length - 1]"
                :pointid="
                  state.points[state.points.length - 1].id == null
                    ? state.points.length - 1
                    : state.points[state.points.length - 1].id
                "
                :max-y="pointMaxY"
                :min-y="pointMinY"
                :min-x="xBezierLimits[state.points.length - 1].left"
                :max-x="xMax"
                :start-x-value="startXValue"
                :end-x-value="endXValue"
                :bezier-min-x="xBezierLimits[state.points.length - 1].left"
                :bezier-max-x="xBezierLimits[state.points.length - 1].right"
                :grid-column-width="widthStepSize"
                :deletable="false"
                :y-factor="state.yFactor"
                :move-dimensions="moveDimensions"
                :small="state.curveType == 'formula'"
                @move-point="setPoint(state.points.length - 1, $event)"
                @dragend="update()"
                @contextmenu="openContextMenu($event)"
              />
            </template>
          </template>
        </g>
      </g>
    </svg>

    <context-menu
      v-if="contextMenuData.actions.length > 0"
      :x="contextMenuData.x"
      :y="contextMenuData.y"
      @close="closeContextMenu"
    >
      <template v-for="action in contextMenuData.actions" :key="action.title">
        <button
          class="context-menu__entry"
          :class="action.class"
          @click.prevent.stop="doContextMenuAction($event, action)"
        >
          {{ action.title }}
        </button>
      </template>
    </context-menu>

    <p
      v-if="isGraphEditable && contextMenuEnabled"
      class="input-curve__curvehelptext"
    >
      <lucide-icon name="info" /><span>{{
        $t("texts.curveEditorHelpText")
      }}</span>
    </p>

    <div>
      <!--
        yFactor: {{ yFactor }}
        Y_MAX: {{ Y_MAX }}
        Y_MIN: {{ Y_MIN }}
        missingVerticalAmount: {{ missingVerticalAmount }}
      -->
      <!--
        offsetY: {{ offsetY }}<br>
        topBound: {{ topBound }}<br>
        bottomBound: {{ bottomBound }}<br>
        curveBounds: {{ curveBounds }}
      -->

      <!--
      <pre>yAtValue: {{ yAtValue }}</pre>
      <pre>derivativeAtValue: {{ derivativeAtValue }}</pre>

      <pre>pointMaxY: {{ pointMaxY }}</pre>
      -->

      <!--{{ mouseX }}: {{ xAtMouse }}-->
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, watch, ref } from "vue";
import { evaluate } from "mathjs";
import { cloneDeep, clamp } from "lodash-es";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import { getFormulaPoints } from "./Relation";

import { clampNumber, vectorLength } from "@/helpers/utility/MathHelper";

import GraphModel from "@editor/items/relation/GraphModel";

import InputDropdown from "@core/input/InputDropdown.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";

import ContextMenu from "@core/ui/ContextMenu.vue";
import InputText from "@core/input/InputText.vue";
import InputCurveHandle from "./InputCurveHandle.vue";

import { evaluateRelationFormula } from "./Relation";
import { useSettingsStore } from "@editor/store/settings.store";

import type { GraphPoint } from "./GraphPoint";
import type { CurveFormType } from "./RelationItem";
import type { ContextmenuAction } from "@core/ui/rightClickMenu.store";

interface Props {
  pointList: GraphPoint[];
  /**
   * The value of the outgoing element (x-value)
   */
  value?: number;

  inverse?: boolean;
  type?: CurveFormType;
  minHeight?: number;
  curveEditable?: boolean;
  typeEditable?: boolean;
  contextMenuEnabled?: boolean;
  fixedStart?: boolean;
  xMin: number | null;
  xMax: number | null;
  moveDimensions?: "x" | "y" | "xy";
  colorAreasVisible?: boolean;
  formula?: string;
}

const props = withDefaults(defineProps<Props>(), {
  value: undefined,
  inverse: false,
  type: "linear",
  minHeight: 100,
  curveEditable: true,
  typeEditable: true,
  contextMenuEnabled: true,
  fixedStart: false,
  moveDimensions: "xy",
  colorAreasVisible: false,
  formula: "1 * x"
});

const emit = defineEmits([
  "hovervalue",
  "change",
  "update:pointList",
  "update:curveType",
  "update:formula"
]);

const { t, locale } = useI18n();
const toast = useToast();

const WIDTH = 100;
const Y_MIN = -100;
const Y_MAX = 100;
const X_MIN = -50;
const X_MAX = 50;

const state = reactive({
  points: [] as GraphPoint[],

  curveType: "linear" as CurveFormType,
  valueAtMouse: 0,
  mouseX: 0,
  offsetY: 0,

  yFactor: 1,

  localFormula: "1 * x",

  formulaHasError: false
});

const graphRef = ref<SVGSVGElement>();

const curveNames = reactive({
  linear: t("curveTypes.linear"),
  saturation: t("curveTypes.saturation"),
  exponential: t("curveTypes.exponential"),
  s: t("curveTypes.s"),
  plateau: t("curveTypes.plateau"),
  gauss: t("curveTypes.gauss"),
  formula: t("curveTypes.formula"),
  custom: t("curveTypes.custom")
});

const scaledPoints = computed(() => {
  return state.points.map((point) => ({
    ...point,
    x: scaleXValue(point.x),
    y: point.y / state.yFactor
  }));
});

const startSlopeLine = computed(() => {
  const firstPoint = scaledPoints.value[0];
  const distance = firstPoint.x;
  let slope = 0;
  if (firstPoint.bezierIn != null) {
    const point = state.points[0];
    const bezierPoint = {
      x: scaleXValue(point.x + point.bezierIn!.x),
      y: (point.y + point.bezierIn!.y) / state.yFactor
    }
    const y = (firstPoint.y - bezierPoint.y);
    const x = (firstPoint.x - bezierPoint.x);
    if (x != 0) {
      slope = y / x;
    }
  }
  return {
    x1: 0,
    y1: firstPoint.y - distance * slope,
    x2: firstPoint.x,
    y2: firstPoint.y
  };
});

const endSlopeLine = computed(() => {
  const lastPoint = scaledPoints.value[scaledPoints.value.length - 1];
  const distance = 100 - lastPoint.x;
  let slope = 0;
  if (lastPoint.bezierOut != null) {
    const point = state.points[state.points.length - 1];
    const bezierPoint = {
      x: scaleXValue(point.x + point.bezierOut!.x),
      y: (point.y + point.bezierOut!.y) / state.yFactor
    }
    const y = (lastPoint.y - bezierPoint.y);
    const x = (lastPoint.x - bezierPoint.x);
    if (x != 0) {
      slope = y / x;
    }
  }
  return {
    x1: lastPoint.x,
    y1: lastPoint.y,
    x2: 100,
    y2: lastPoint.y + distance * slope
  };
});

const isGraphEditable = computed(() => {
  return props.curveEditable; // && state.curveType != "formula";
});

const graphModel = computed(() => {
  try {
    return new GraphModel(state.points);
  } catch (error) {
    console.warn("Error creating graph model:", error);
    return null;
  }
});

const startPoint = computed(() => {
  return graphModel.value?.pStart ?? ({ x: 0, y: 0 } as GraphPoint);
});

const endPoint = computed(() => {
  return graphModel.value?.pEnd ?? ({ x: 100, y: 0 } as GraphPoint);
});

const pathDescription = computed(() => {
  if (graphModel.value == null) return "";
  return graphModel.value.getDescription({
    yFactor: state.yFactor,
    xMin: startXValue.value,
    xMax: endXValue.value
  });
});

// ======================
// DIMENSIONS

const curveBounds = computed(() => {
  if (graphModel.value == null) return ({ top: 0, bottom: 0, left: 0, right: 0 });
  return graphModel.value.bounds;
});

const height = computed(() => {
  const top = Math.min(curveBounds.value.top, Y_MAX);
  const bottom = Math.max(curveBounds.value.bottom, Y_MIN);
  const curveHeight = Math.ceil((top - bottom) / 10) * 10;
  return Math.max(props.minHeight, curveHeight);
});

const limitedWidth = computed(() => {
  if (props.xMin == null || props.xMax == null) return null;
  return Math.min(props.xMax, 1_000_000_000_000_000) - Math.max(props.xMin, -1_000_000_000_000_000);
});
const width = computed(() => {
  if (limitedWidth.value != null) return limitedWidth.value;
  return Math.max(endXValue.value - startXValue.value, 10);
});

const padding = reactive({
  top: 10,
  left: 16,
  right: 10,
  bottom: computed(() => {
    return labelsRotated.value ? 20 : 10;
  })
});

/**
 * size of one vertical step
 */
const heightSteps = computed(() => {
  const steps = [];
  for (let i = 0; i <= height.value; i += 10) {
    steps.push(i);
  }
  return steps;
});

const startXValue = computed(() => {
  if (props.xMin != null) return props.xMin;
  return Math.min(X_MIN, curveBounds.value.left);
});
const endXValue = computed(() => {
  if (props.xMax != null) return Math.min(props.xMax, 1_000_000_000_000_000);
  return Math.max(X_MAX, curveBounds.value.right);
});

const xStepSize = computed(() => {
  if (width.value < 1) return width.value / 10;
  const optimalSize = Math.ceil(width.value / 10);
  return width.value <= 10 ? 1 : optimalSize;
});

const widthStepsAmount = computed(() => {
  if (xStepSize.value == 0) return 1;
  return width.value / xStepSize.value;
});

const widthStepSize = computed(() => {
  return WIDTH / widthStepsAmount.value;
});

const widthSteps = computed(() => {
  const steps = [];
  for (let i = 0; i <= widthStepsAmount.value; i++) {
    steps.push(i * widthStepSize.value);
  }
  return steps;
});

const verticalNumbers = computed(() => {
  let numbers = [];
  for (
    let i = topBound.value + roundedOffsetY.value;
    i >= bottomBound.value + roundedOffsetY.value;
    i -= 10
  ) {
    numbers.push(formatNumberLabel(i * state.yFactor));
  }
  return numbers;
});

const horizontalNumbers = computed((): (string | number)[] => {
  if (xStepSize.value == 0) return [startXValue.value, endXValue.value];

  let numbers = [];
  for (let i = startXValue.value; i <= endXValue.value; i += xStepSize.value) {
    numbers.push(formatNumberLabel(i));
  }
  return numbers;
});

const labelsRotated = computed(() => {
  return (
    Math.abs(startXValue.value) > 100 ||
    Math.abs(endXValue.value) > 100 ||
    Math.abs(startXValue.value) <= 0.001 ||
    Math.abs(endXValue.value) <= 0.001
  );
});

// bounds
const topBound = computed(() => {
  return Y_MAX + missingVerticalAmount.value;
});
const bottomBound = computed(() => {
  return Y_MIN - missingVerticalAmount.value;
});

/**
 * create point limits to allow a max range between the points.
 */
const pointMaxY = computed(() => {
  return Y_MAX + 10;
});
const pointMinY = computed(() => {
  return Y_MIN - 10;
});

// const pointMaxX = computed(() => {
//   return X_MAX + 10;
// });
// const pointMinX = computed(() => {
//   return X_MIN - 10;
// });

/**
 * The Y-offset in a step of {stepSize} (usually 10).
 */
const roundedOffsetY = computed(() => {
  const absOffset = Math.abs(state.offsetY);
  if (state.yFactor < 1) {
    return Math.ceil(absOffset) * 10 * Math.sign(state.offsetY);
  }
  return Math.ceil(absOffset / 10) * 10 * Math.sign(state.offsetY);
});

const missingVerticalAmount = computed(() => {
  let missingAmount =
    (height.value - (Math.abs(Y_MIN) + Math.abs(Y_MAX))) /
    2;
  return Math.floor(missingAmount / 10) * 10;
});

const horizontalAreas = computed(() => {
  return useSettingsStore().targetValueColors;
});

/**
 * Build limits for the bezier positions of each of the curve points.
 * Each bezier can be extended to one unit next to the next point
 * position at max.
 * e.g. (p0 is at 5, p1 is at 30 ---> p0.bezierOut.x can be at max 29)
 * @returns limit positions for the beziers, uses the same order as state.points
 */
const xBezierLimits = computed(() => {
  const limits = [];
  for (let i = 0; i < state.points.length; i++) {
    let left: number | null = 0;
    let right: number | null = 0;

    // LEFT
    // on first point, use first point
    if (i === 0) {
      left = props.xMin;
    } else {
      const previousPoint = state.points[i - 1];
      left = previousPoint.x + 0.001;
    }

    // RIGHT
    // on last point, use last point
    if (i === state.points.length - 1) {
      right = props.xMax;
    } else {
      const nextPoint = state.points[i + 1];
      right = nextPoint.x - 0.001;
    }
    limits.push({ left, right });
  }
  return limits;
});

// ======================
// CONTEXT MENUS

const contextMenuData = reactive({
  x: 0,
  y: 0,
  actions: [] as ContextmenuAction[]
});

function onMainContextMenu(event: MouseEvent) {
  if (!props.contextMenuEnabled) return;

  if (!event.defaultPrevented) {
    const x = state.mouseX;
    openContextMenu({
      x: event.clientX,
      y: event.clientY,
      actions: [
        {
          title: t("actions.addRelationCurvePoint"),
          callback: () => {
            addPoint(x);
          }
        }
      ]
    });
  }

  event.preventDefault();
}
function openContextMenu(event: { x: number; y: number; actions: ContextmenuAction[] }) {
  if (!props.curveEditable) return;
  contextMenuData.x = event.x;
  contextMenuData.y = event.y;
  contextMenuData.actions = event.actions;
}
function closeContextMenu() {
  contextMenuData.actions = [];
}
function doContextMenuAction(mouseEvent: MouseEvent, action: ContextmenuAction) {
  mouseEvent.preventDefault();
  mouseEvent.stopPropagation();
  action.callback();
  closeContextMenu();
}

// ======================

const curves = computed(() => {
  const extrema = graphModel.value ? graphModel.value.extremePoints.map((p) => p.y) : [0];
  const minExtremum = Math.min(...extrema);
  const maxExtremum = Math.max(...extrema);

  const curveEndX = endPoint.value.x;
  const xMax = endXValue.value ?? curveEndX;
  
  const startEndDistanceX = endPoint.value.x - startPoint.value.x;
  const curveHeight =
    Math.max(maxExtremum, startPoint.value.y) -
    Math.min(minExtremum, startPoint.value.y);
  const centerX = Math.max(
    startPoint.value.x + startEndDistanceX / 2,
    startPoint.value.x + 1
  );
  const safeEndX = Math.min(
    Math.max(endPoint.value.x, centerX + 1),
    xMax
  );

  let endExtremum = maxExtremum;
  if (startPoint.value.y == maxExtremum) {
    endExtremum = minExtremum;
  }

  return {
    linear: [
      {
        id: 0,
        x: startPoint.value.x,
        y: startPoint.value.y,
        bezierOut: {
          x: 0,
          y: 0
        }
      },
      {
        id: 1,
        x: endPoint.value.x,
        y: endExtremum,
        bezierIn: {
          x: 0,
          y: 0
        }
      }
    ],
    custom: [
      {
        id: 0,
        x: Math.min(startPoint.value.x, xMax - 5),
        y: startPoint.value.y,
        bezierOut: {
          x: 0,
          y: 0
        }
      },
      {
        id: 1,
        x: Math.min(
          startPoint.value.x + Math.max(startEndDistanceX * 0.2, 1),
          xMax - 4
        ),
        y: startPoint.value.y + Math.round(curveHeight * 0.2),
        bezierIn: { x: 0, y: 0 },
        bezierOut: { x: 0, y: 0 }
      },
      {
        id: 2,
        x: Math.min(
          startPoint.value.x + Math.max(startEndDistanceX * 0.4, 2),
          xMax - 3
        ),
        y: startPoint.value.y + Math.round(curveHeight * 0.4),
        bezierIn: { x: 0, y: 0 },
        bezierOut: { x: 0, y: 0 }
      },
      {
        id: 3,
        x: Math.min(
          startPoint.value.x + Math.max(startEndDistanceX * 0.6, 3),
          xMax - 2
        ),
        y: startPoint.value.y + Math.round(curveHeight * 0.6),
        bezierIn: { x: 0, y: 0 },
        bezierOut: { x: 0, y: 0 }
      },
      {
        id: 4,
        x: Math.min(
          startPoint.value.x + Math.max(startEndDistanceX * 0.8, 4),
          xMax - 1
        ),
        y: startPoint.value.y + Math.round(curveHeight * 0.8),
        bezierIn: { x: 0, y: 0 },
        bezierOut: { x: 0, y: 0 }
      },
      {
        id: 5,
        x: Math.min(
          Math.max(endPoint.value.x, startPoint.value.x + 5),
          xMax
        ),
        y: endPoint.value.y,
        bezierIn: { x: 0, y: 0 },
        bezierOut: { x: 0, y: 0 }
      }
    ],
    s: [
      {
        id: 0,
        x: startPoint.value.x,
        y: startPoint.value.y,
        bezierOut: {
          x: Math.max(startEndDistanceX / 2, 1),
          y: 0
        }
      },
      {
        id: 1,
        x: endPoint.value.x,
        y: endExtremum,
        bezierIn: {
          x: Math.min(-startEndDistanceX / 2, -1),
          y: 0
        }
      }
    ],
    saturation: [
      {
        id: 0,
        x: startPoint.value.x,
        y: startPoint.value.y,
        bezierOut: {
          x: 0,
          y: 0
        }
      },
      {
        id: 1,
        x: endPoint.value.x,
        y: endExtremum,
        bezierIn: {
          x: -startEndDistanceX + 1,
          y: 0
        }
      }
    ],
    exponential: [
      {
        id: 0,
        x: startPoint.value.x,
        y: startPoint.value.y,
        bezierOut: {
          x: startEndDistanceX - 1,
          y: 0
        }
      },
      {
        id: 1,
        x: endPoint.value.x,
        y: endExtremum,
        bezierIn: {
          x: 0,
          y: 0
        }
      }
    ],
    plateau: [
      {
        id: 0,
        x: startPoint.value.x,
        y: startPoint.value.y,
        bezierOut: {
          x: 0,
          y: 0
        }
      },
      {
        id: 1,
        x: centerX,
        y: curveHeight / 2,
        bezierIn: {
          x: -(startEndDistanceX / 2),
          y: 0
        },
        bezierOut: {
          x: startEndDistanceX / 2,
          y: 0
        }
      },
      {
        id: 2,
        x: safeEndX,
        y: endExtremum,
        bezierIn: {
          x: 0,
          y: 0
        }
      }
    ],
    gauss: [
      {
        id: 0,
        x: startPoint.value.x,
        y: startPoint.value.y,
        bezierOut: {
          x: startEndDistanceX / 5,
          y: 0
        }
      },
      {
        id: 1,
        x: centerX,
        y: endExtremum,
        bezierIn: {
          x: -startEndDistanceX / 5,
          y: 0
        },
        bezierOut: {
          x: startEndDistanceX / 5,
          y: 0
        }
      },
      {
        id: 2,
        x: safeEndX,
        y: startPoint.value.y,
        bezierIn: {
          x: -startEndDistanceX / 5,
          y: 0
        }
      }
    ],
    formula: getInputFormulaPoints(props.formula)
  };
});

/**
 * This key is used to check if the size of the
 * SVG changed. It can then be watched to trigger
 * following recalculations.
 */
const svgCTMKey = computed(() => {
  return `${WIDTH + padding.left + padding.right} ${
    height.value + padding.bottom + padding.top
  }`;
});

const svgCTM = computed(() => {
  const graphSVG = graphRef.value;
  if (graphSVG == null) return null;

  // Add a dependency to the dimensions of the SVG
  if (svgCTMKey.value == "") return null;

  return graphSVG.getScreenCTM();
});

watch(
  () => props.type,
  (type) => {
    state.curveType = type;
    if (type === "formula") {
      setStatePoints(getInputFormulaPoints(props.formula));
    } else {
      setStatePoints(cloneDeep(props.pointList));
    }
    state.yFactor = getOptimalYFactor();
    updateOffset(true);
  },
  { immediate: true }
);

watch(
  () => props.pointList,
  (points) => {
    if (state.curveType == "formula") {
      setStatePoints(getInputFormulaPoints(props.formula));
    } else {
      setStatePoints(cloneDeep(points));
    }
    state.yFactor = getOptimalYFactor();
    updateOffset(true);
  },
  { immediate: true, deep: true }
);
watch(
  () => state.yFactor,
  () => {
    updateOffset(true);
  }
);
watch(
  () => props.formula,
  (val) => {
    if (state.localFormula != val) {
      state.localFormula = val;

      if (state.curveType === "formula") {
        setStatePoints(getInputFormulaPoints(val));
      }
    }
  },
  { immediate: true }
);

onMounted(() => {
  updateOffset(true);

  emit("hovervalue", {
    x: 50,
    y: Math.round(getValueAtX(50)),
    x2: 0,
    y2: Math.round(getValueAtX(0))
  });
});

// ======================
// METHODS

function setStatePoints(newPoints: GraphPoint[]) {
  console.log("set state points", [...newPoints]);

  // OPTION 1: Are there no points?
  if (newPoints.length == 0) {
    // Prevent that. We need points.

    if (props.xMin == null && props.xMax == null) {
      console.log("no points, no limits");
      state.points = [
        {
          id: 0,
          x: 0,
          y: 0
        },
        {
          id: 1,
          x: 100,
          y: 0
        }
      ];
    } else if (props.xMin == props.xMax) {
      console.log("no points, but limits are the same");
      state.points = [
        {
          id: 0,
          x: props.xMin!,
          y: 0
        }
      ];
    } else {
      console.log("no points, but limits are set");
      // we know that at least one of the values is set.
      const min = props.xMin ?? props.xMax! - 100;
      const max = props.xMax ?? props.xMin! + 100;

      state.points = [
        {
          id: 0,
          x: min,
          y: 0
        },
        {
          id: 1,
          x: max,
          y: 0
        }
      ];
    }
  } 
  // OPTION 2: Are there limits to the x values?
  else if (newPoints.length > 0 && (props.xMin != null || props.xMax != null)) {
    if (props.xMin == props.xMax) {
      // Only one point given, set it inside the limit.
      state.points = [
        {
          id: newPoints[0].id,
          x: props.xMin!,
          y: newPoints[0].y
        }
      ];
      console.log("one point only", state.points);
    }
    else if (newPoints.length == 1) {
      // Only one point given, but the width allows more?
      // add points.

      // we know that at least one of the values is set.
      const min = props.xMin ?? props.xMax! - 100;
      const max = props.xMax ?? props.xMin! + 100;

      state.points = [
        {
          id: newPoints[0].id,
          x: min,
          y: newPoints[0].y,
          bezierOut: {
            x: newPoints[0]?.bezierOut?.x || 0,
            y: newPoints[0]?.bezierOut?.y || 0
          }
        },
        {
          id: (newPoints[0].id || 0) + 1,
          x: max,
          y: newPoints[0].y,
          bezierIn: {
            x: newPoints[0]?.bezierIn?.x || 0,
            y: newPoints[0]?.bezierIn?.y || 0
          }
        }
      ];
      
      console.log("one point to two points", state.points);
    }
    else {
      // We received points and there are limits.
      // Check if the points are within the limits.

      const graphModel = new GraphModel(newPoints);

      let points = cloneDeep(newPoints);

      const min = props.xMin;
      const max = props.xMax;

      if (min != null) {
        const pointsOutsideRange = points.filter((p) => p.x < min);
        if (pointsOutsideRange.length > 0) {
          // remove all points outside the range.
          points.splice(0, pointsOutsideRange.length);
          console.log("cut off smaller points", pointsOutsideRange);

          let pointAtMin = points.find((p) => p.x == min);
          if (pointAtMin == null) {
            const newMinPointY = graphModel.valueAtX(min);
            pointAtMin = {
              id: Math.random(),
              x: min,
              y: newMinPointY,
              bezierIn: {
                x: 0,
                y: 0
              },
              bezierOut: {
                x: 0,
                y: 0
              }
            };
            // TODO: calculate bezier out.

            points.unshift(pointAtMin);
          }
        }
      }
      
      if (max != null) {
        const pointsOutsideRange = points.filter((p) => p.x > max);
        if (pointsOutsideRange.length > 0) {
          // remove all points outside the range.
          points.splice(points.length - pointsOutsideRange.length);
          console.log("cut off larger points", pointsOutsideRange);

          let pointAtMax = points.find((p) => p.x == max);
          if (pointAtMax == null) {
            const newMaxPointY = graphModel.valueAtX(max);
            pointAtMax = {
              id: Math.random(),
              x: max,
              y: newMaxPointY,
              bezierIn: {
                x: 0,
                y: 0
              },
              bezierOut: {
                x: 0,
                y: 0
              }
            };
            
            points.push(pointAtMax);
          }
        }
      }

      state.points = points;
      
      console.log("points limited", state.points);
    }
  }
  // No limits and points are given? Use them.
  else {
    state.points = newPoints;
    console.log("unlimited points", state.points);
  }
}

function getValueAtX(x: number) {
  if (state.curveType === "formula") {
    return evaluateRelationFormula(props.formula, x);
  }
  if (graphModel.value == null) return 0;
  return graphModel.value.valueAtX(x);
}

function formatNumberLabel(num: number) {
  if (Math.abs(num) < 100 && Math.abs(num) >= 1) return num + "";

  return new Intl.NumberFormat(locale.value, {
    notation: "compact",
    compactDisplay: "short"
  }).format(num);
}

/**
 * get the current value of the graph at mouse position
 */
function setXAtMouse(event: MouseEvent) {
  // get x:
  const screenCTM = svgCTM.value;
  if (!screenCTM) return;

  const pt = new DOMPoint(event.clientX, event.clientY);
  const localPoint = pt.matrixTransform(screenCTM.inverse());

  // get the Y value at X:
  const localX = Math.round(localPoint.x - padding.left);
  const x = clamp(scaleXValueBack(localX), startXValue.value, endXValue.value);
  const y = getValueAtX(x);

  // get an offset value for comparison
  const OFFSET = 10;
  let x2 = x - OFFSET;
  if (x2 < 0) x2 = x + OFFSET;
  const y2 = getValueAtX(x2);

  if (x !== state.mouseX) {
    state.mouseX = x;
    state.valueAtMouse = y;

    emit("hovervalue", {
      x: Math.round(x),
      y: Math.round(state.valueAtMouse * 100) / 100,
      x2: Math.round(x2),
      y2: Math.round(y2 * 100) / 100
    });
  }
}

function onSelectCurveType({ key }: { key: CurveFormType }) {
  state.curveType = key;

  // create curve points
  setStatePoints(curves.value[key].slice(0));

  // emit the changes.
  update(key);
}

function setPoint(index: number, newPoint: GraphPoint) {
  state.points[index] = newPoint;

  // this.updateOffset();
  limitAllBeziers();
}

function limitAllBeziers() {
  for (let i = 0; i < state.points.length; i++) {
    limitBeziers(state.points[i], i);
  }
}

function limitBeziers(point: GraphPoint, index: number) {
  const MIN_LOCAL_DISTANCE = 5;
  const limits = xBezierLimits.value[index];
  // update beziers:
  if (point.bezierIn) {
    const bezierIn = point.bezierIn; 
    const x = clampNumber(point.x + bezierIn.x, limits.left, limits.right);
    bezierIn.x = x - point.x;

    // get local distance
    const localBezier = {
      x: scaleXValue(bezierIn.x),
      y: bezierIn.y / state.yFactor
    };

    const distance = vectorLength(localBezier);
    if (distance < MIN_LOCAL_DISTANCE) {
      bezierIn.x = 0;
      bezierIn.y = 0;
    }
  }
  if (point.bezierOut) {
    const bezierOut = point.bezierOut;
    const x = clampNumber(point.x + bezierOut.x, limits.left, limits.right);
    bezierOut.x = x - point.x;

    // get local distance
    const localBezier = {
      x: scaleXValue(bezierOut.x),
      y: bezierOut.y / state.yFactor
    };

    const distance = vectorLength(localBezier);
    if (distance < MIN_LOCAL_DISTANCE) {
      bezierOut.x = 0;
      bezierOut.y = 0;
    }
  }
}

function updateOffset(recenter = false) {
  const top = Math.min(curveBounds.value.top, Y_MAX * state.yFactor) / state.yFactor;
  const bottom = Math.max(curveBounds.value.bottom, Y_MIN * state.yFactor) / state.yFactor;
  const scaledTopBound = topBound.value;
  const scaledBottomBound = bottomBound.value;

  if (top >= scaledTopBound) {
    if (recenter) {
      state.offsetY = top - scaledTopBound;
    } else {
      state.offsetY = Math.max(state.offsetY, top - scaledTopBound);
    }
  } else if (bottom <= scaledBottomBound) {
    if (recenter) {
      state.offsetY = bottom - scaledBottomBound;
    } else {
      state.offsetY = Math.min(state.offsetY, bottom - scaledBottomBound);
    }
  } else if (recenter) {
    state.offsetY = 0;
  }

  if (state.yFactor < 1) {
    state.offsetY = state.offsetY * state.yFactor;
  }
}

function update(newCurveType: CurveFormType = "custom") {
  limitAllBeziers();
  const newPoints = cloneDeep(state.points);
  emit("update:pointList", newPoints);
  emit("update:curveType", newCurveType);
  emit("change", {
    points: newPoints,
    type: newCurveType
  });
}

function getOptimalYFactor() {
  if (graphModel.value == null) return 1;
  const highestValue = graphModel.value.absoluteMaxInfluence;
  const baseMax = Y_MAX;
  const rawYFactor = highestValue / baseMax;
  if (rawYFactor <= 0.1) {
    return 0.1;
  }
  const optimalYFactor = Math.ceil(rawYFactor);
  if (optimalYFactor <= 1) return 1;
  const exponent = Math.log10(optimalYFactor);
  const squaredYFactor = Math.pow(10, Math.ceil(exponent));
  // the factor should ALWAYS be a square of 10.
  return squaredYFactor;
}

function onRightClick(event: MouseEvent) {
  if (!event.defaultPrevented) {
    addPoint(state.mouseX);

    event.preventDefault();
  }
}

/**
 * @param x the x-coordinate where to add the point
 */
function addPoint(x: number) {
  // don't create a point if there is one at that position.
  if (state.points.findIndex((p) => p.x === x) != -1) {
    toast.error(t("errors.relation.pointAlreadyExists"));
    return false;
  }

  // make sure that the new point gets an id no other point has
  const fallbackId = state.points.length + 1;
  const newPointId = state.points.reduce((id, p) => {
    if (p.id && p.id >= id) {
      return p.id + 1;
    }
    return id;
  }, fallbackId);

  // create the point right on the value line.
  const newPoint: GraphPoint = {
    x: x,
    y: state.valueAtMouse,
    id: newPointId
  };

  // find the index in the array where to put the
  // new point in
  let pointIndex = 0;
  for (let i = 0; i < state.points.length; i++) {
    if (state.points[i].x < x) pointIndex = i + 1;
    else break;
  }

  // make sure the right points have bezierIn
  // and bezierOut handles
  if (pointIndex > 0) {
    newPoint.bezierIn = {
      x: 0,
      y: 0
    };
  } else {
    state.points[0].bezierIn = {
      x: 0,
      y: 0
    };
  }
  if (pointIndex < state.points.length) {
    newPoint.bezierOut = {
      x: 0,
      y: 0
    };
  } else {
    state.points[state.points.length - 1].bezierOut = {
      x: 0,
      y: 0
    };
  }

  // add the new point in the points array
  state.points.splice(pointIndex, 0, newPoint);
  update();

  return newPoint;
}

function deletePoint(index: number) {
  state.points.splice(index, 1);
  update();
}

/**
 * From min/max-range to 0-100
 */
function scaleXValue(val: number) {
  if (width.value == 0) return 0;
  return ((val - startXValue.value) / width.value) * WIDTH;
}

/**
 * From 0-100 range to min/max-range
 */
function scaleXValueBack(val: number) {
  if (width.value == 0) return startXValue.value;
  return (val / WIDTH) * width.value + startXValue.value;
}

// function scaleX(points: GraphPoint[]) {
//   return points.map((point) => ({
//     ...point,
//     x: scaleXValue(point.x),
//     y: point.y
//   }));
// }

// function scaleXBack(points: GraphPoint[]) {
//   const newPoints = points.map((point) => ({
//     ...point,
//     x: scaleXValueBack(point.x),
//     y: point.y
//   }));
//   return newPoints;
// }

function updateFormula(newFormula: string) {
  state.localFormula = newFormula;
  try {
    evaluate(newFormula, { x: 0 });
    evaluate(newFormula, { x: 5 });
    evaluate(newFormula, { x: -30 });
    evaluate(newFormula, { x: 50000 });
    state.formulaHasError = false;
    emit("update:formula", newFormula);

    if (state.curveType === "formula") {
      setStatePoints(getInputFormulaPoints(newFormula));
      update("formula");
    }
  } catch (error) {
    console.warn("math error", newFormula, error);
    state.formulaHasError = true;
    return;
  }
}

function getInputFormulaPoints(formula: string) {
  return getFormulaPoints(formula, xStepSize.value / 4, startXValue.value, endXValue.value);
}
</script>

<style lang="scss">
.input-curve {
  max-width: 30rem;

  &__curvehelptext {
    margin-top: 0.25rem;
    font-size: 12px;
    display: flex;
    gap: 0.25rem;

    .icon {
      font-size: 14px;
      margin-top: 2px;
    }
  }

  &__graph {
    width: 100%;
    position: relative;
    overflow: visible;
  }

  &__line {
    fill: none;
    stroke: var(--color-action);
    stroke-width: 3px;
  }

  &__raster-line {
    stroke: var(--color-text-light);
    stroke-width: 1px;
    opacity: 0.5;
    shape-rendering: crispEdges;
  }

  &__line_value {
    stroke: red;
    stroke-width: 1px;
    opacity: 0.7;
  }
  &__mouseline {
    stroke: var(--color-action);
    stroke-width: 1px;
    opacity: 0.25;
  }

  &__border-line {
    stroke: var(--color-action);
    stroke-width: 1px;
  }

  &__label {
    fill: var(--color-text);
    font-size: 5px;
    &_h {
      text-anchor: middle;
      &.-rotated {
        text-anchor: start;
        transform: rotate(45deg);
      }
    }
    &_v {
      text-anchor: end;
    }
  }
}
</style>
